import React, { useState } from "react";
import InputBox from "../../../../../common/input/InputBox";
import RedClose from "../../../../../icons/Red-Close";
import Button from "../../../../../common/buttons/Button";
import InputBoxSelect from "../../../../../common/input/InputBoxSelect";
import ArrowBackward from "../../../../../icons/ArrowBackward";
import { useSelector } from "react-redux";
import { MdEdit } from "react-icons/md";
import { IoIosAddCircle } from "react-icons/io";
import { getMedDetails } from "../../../../../../api/settings/Medicines/medicines.js";
import { showErrorToast } from "../../../../../../utils/toaster.js";

// Round money to 2 decimals — kills float drift (e.g. 5.6 * 4 = 22.400000000000002).
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
// Round the payable to the nearest whole number (no decimals): 22.4 -> 22, 22.5 -> 23.
// Same policy as the CREATE bill page — Total + Balance only; line amounts stay exact.
const roundWhole = (n) => Math.round(Number(n) || 0);

const ServiceMedicineInfo = ({
  formik,
  addRow,
  removeRow,
  treatments,
  setSearch,
  handleGetMedDetails,
  edit,
}) => {
  const currentUser = useSelector((state) => state.auth);
  const [showAddQty, setShowQty] = useState([null]);
  const handleInputChange = (index, event) => {
    try {
      const inputValue = parseFloat(event?.target?.value) || 0;

      if (isNaN(inputValue)) {
        console.error("Input value is not a valid number");
        return;
      }
      // Calculate the total amount
      const updatedAmounts = formik.values.treatment_done_info.map((item, i) =>
        i === index ? inputValue : parseFloat(item.amount) || 0
      );

      const newTotalAmount = updatedAmounts.reduce(
        (acc, amount) => acc + parseFloat(amount),
        0
      );
      const discountedTotal =
        parseFloat(newTotalAmount) - (parseFloat(formik.values?.discount) || 0);

      if (!isNaN(newTotalAmount)) {
        /** Set to formik */
        formik.setFieldValue("treatment_amount", round2(newTotalAmount));
        formik.setFieldValue("total_amount", roundWhole(discountedTotal));
        const newBalance =
          parseFloat(newTotalAmount) - formik?.values?.paymentIn?.amountPaid;
        formik.setFieldValue("balance", roundWhole(newBalance));
      } else {
        console.error("Total amount is not a valid number");
      }
    } catch (error) {
      console.error(error);
    }
  };

  /** Treatment quantity change handler */
  const handleQuantityChange = async (index, event) => {
    // Input validation
    if (!event || typeof event !== "number" || event < 1 || !edit) {
      console.error("Invalid input data");
      return;
    }
    const newQuantity = event;
    /** */
    const treatment = formik.values?.treatment_done_info?.[index] || [];
    const newTreatmentTotal = round2(treatment.actualAmount * newQuantity);

    /** Set quantity value on formik */
    formik.setFieldValue(`treatment_done_info.${index}.quantity`, newQuantity);
    /** Set quantity value on formik end */

    /** Set quantity value on formik */
    formik.setFieldValue(
      `treatment_done_info.${index}.amount`,
      newTreatmentTotal
    );
    /** Set quantity value on formik end */
    const values = {
      target: {
        value: treatment.amount,
        quantity: newQuantity,
      },
    };

    handleInputChange(index, values);
  };
  /** Treatment quantity change handler end*/

  const handleAddBatchRow = (index, item) => {
    if (formik?.values?.treatment_done_info?.[index]?.name) {
      setShowQty(() => [...showAddQty, index]);
      handleGetMedDetails(item);
    }
  };

  const handleGetMedDetailsData = (name, index) => {
    getMedDetails({ search: name })
      .then((response) => {
        if (response.data.data.length > 0) {
          const data = response.data.data[0];
          const event = {
            target: {
              value: data.selling_price,
              quantity: 1,
            },
          };
          formik.setFieldValue(
            `treatment_done_info.${index}.amount`,
            data.selling_price
          );
          formik.setFieldValue(
            `treatment_done_info.${index}.actualAmount`,
            data.selling_price
          );
          formik.setFieldValue(
            `treatment_done_info.${index}.quantity`,
            1
          );
          handleInputChange(index, event);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {});
  };

  return (
    <div className="shadow-card rounded-15 ">
      <div
        className={`shadow-card rounded-tl-15 rounded-tr-15 bg-secondary p-3 text-bodyBB text-darkgrey mb-2 `}
      >
        Medicine Info
      </div>
      <div className=" overflow-auto lg:max-h-[300px] overflow-y-auto">
        <table className=" w-screen md:w-full border-separate border-spacing-y-1 min-w-max ">
          <thead className="">
            <tr>
              <th className="border-b pb-2 text-bodyBB text-darkgrey h-[50px]">
                Sl No
              </th>
              <th className="border-b pb-2 text-bodyBB text-darkgrey">
                Medicines
              </th>
              {/* Units column always rendered — every row below has a
                  `quantity` <td>, so the header must not be gated. */}
              <th className="border-b pb-2 text-bodyBB text-darkgrey">
                Units
              </th>
              <th className="border-b pb-2 text-bodyBB text-darkgrey">
                Amount
              </th>
              <th className="border-b pb-2 text-bodyBB text-darkgrey"></th>
            </tr>
          </thead>
          <tbody>
            {formik?.values?.treatment_done_info?.map((item, index) => (
              <tr className="" key={index}>
                <td className="text-center p-2 text-bodyRB text-darkgrey">
                  {index + 1}
                </td>
                <td className="">
                  <InputBoxSelect
                    value={formik?.values?.treatment_done_info?.[index]?.name}
                    error={
                      formik?.touched?.treatment_done_info?.[index]?.name &&
                      formik?.errors?.treatment_done_info?.[index]?.name
                    }
                    disabled={!edit}
                    className={"!h-[45px]"}
                    /** For capturing the selection */
                    onChange={(e) => {
                      const isAlreadySelected =
                        formik?.values?.treatment_done_info?.some(
                          (medicineItem, medicineIndex) =>
                            medicineIndex !== index &&
                            medicineItem?.name?.toLowerCase() ===
                              e?.name?.toLowerCase()
                        );

                      if (isAlreadySelected) {
                        showErrorToast("Medicine already selected.");
                        return;
                      }

                      setSearch("");

                      // Clear all row data before setting new medicine
                      formik.setFieldValue(
                        `treatment_done_info.${index}.quantity`,
                        ""
                      );
                      formik.setFieldValue(
                        `treatment_done_info.${index}.amount`,
                        ""
                      );
                      formik.setFieldValue(
                        `treatment_done_info.${index}.actualAmount`,
                        ""
                      );
                      formik.setFieldValue(
                        `treatment_done_info.${index}.batchDetails`,
                        []
                      );

                      // Set new medicine data
                      formik.setFieldValue(
                        `treatment_done_info.${index}.name`,
                        e.name
                      );
                      handleGetMedDetailsData(e.name, index);
                    }}
                    arrowIcon={true}
                    /** For capturing the input text */
                    handleInputChange={(e) => {
                      setSearch(e.target?.value);
                      formik.setFieldValue(
                        `treatment_done_info.${index}.name`,
                        e.target?.value
                      );
                    }}
                  >
                    {treatments?.map((treatment, index) => (
                      <div
                        key={index}
                        value={{
                          name: treatment.medicine,
                          amount: treatment.charges,
                          quantity: treatment?.quantity,
                        }}
                      >
                        {treatment.medicine}
                      </div>
                    ))}
                  </InputBoxSelect>
                </td>

                <td className={`p-2 !w-[78px]`}>
                  <div className="relative flex items-center">
                    <InputBox
                      className={"h-[45px] !w-[78px] pr-7"}
                      disabled={!edit}
                      onWheel={(e) => e.target.blur()} // Prevent number input scroll
                      onKeyDown={(e) => {
                        // Prevent up/down arrow keys from incrementing/decrementing
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        // Prevent negative values and non-numeric input
                        const value = e.target.value;
                        if (
                          value === "" ||
                          (!isNaN(value) && Number(value) >= 0)
                        ) {
                          formik.handleChange(e);
                          handleQuantityChange(index, Number(value));
                        }
                      }}
                      name={`treatment_done_info.${index}.quantity`}
                      value={item?.quantity}
                      type="text" // Use text to avoid browser arrows, but still validate as number
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />

                    <div className="absolute  right-2 top-1/2 -translate-y-1/2 flex flex-col bg-white rounded-15 shadow-sm z-10">
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => {
                          const value = !isNaN(Number(item?.quantity))
                            ? Number(item?.quantity) + 1
                            : 1;
                          handleQuantityChange(index, value);
                        }}
                        className="w-4 h-4 rotate-[90deg] rounded-full flex items-center justify-center cursor-pointer"
                      >
                        <ArrowBackward />
                      </button>
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => {
                          const value =
                            !isNaN(Number(item?.quantity)) &&
                            Number(item.quantity) - 1 > 1
                              ? Number(item?.quantity) - 1
                              : 1;
                          handleQuantityChange(index, value);
                        }}
                        className="w-4 h-4 rotate-[270deg] rounded-full flex items-center justify-center cursor-pointer"
                      >
                        <ArrowBackward />
                      </button>
                    </div>
                  </div>
                </td>
                <td className="p-1 !w-[78px]">
                  <InputBox
                    className={"h-[45px] !w-[78px]"}
                    name={`treatment_done_info.${index}.amount`}
                    type={"number"}
                    step="0.1"
                    value={item.amount}
                    disabled={!edit}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Restrict to one decimal place
                      if (value && value.includes(".")) {
                        const parts = value.split(".");
                        if (parts[1] && parts[1].length > 1) {
                          // Round to one decimal place
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue)) {
                            value = numValue.toFixed(1);
                            // Create a new event with the rounded value
                            const roundedEvent = {
                              ...e,
                              target: {
                                ...e.target,
                                value: value,
                              },
                            };
                            formik.setFieldValue(
                              `treatment_done_info.${index}.amount`,
                              value
                            );
                            handleInputChange(index, roundedEvent);
                            return;
                          }
                        }
                      }
                      formik.handleChange(e);
                      handleInputChange(index, e);
                    }}
                    error={
                      formik.touched.treatment_done_info?.[index]?.amount &&
                      formik.errors.treatment_done_info?.[index]?.amount
                    }
                  />
                </td>

                {edit && (
                  <td className="">
                    <div className="flex gap-1 items-center">
                      {!formik?.values?.treatment_done_info?.[index]
                        ?.batchDetails?.length > 0 && (
                        <Button
                          onClick={() => handleAddBatchRow(index, item)}
                          type={"secondary"}
                          action={"button"}
                          className={"py-[8px] w-[86px] text-bodyBB"}
                        >
                          Select Stock
                        </Button>
                      )}

                      {formik?.values?.treatment_done_info?.[index]
                        ?.batchDetails?.length > 0 && (
                        <div className="w-fit">
                          <Button
                            onClick={() => handleAddBatchRow(index, item)}
                            type={"primary"}
                            disabled={!edit}
                            action={"button"}
                            className={"py-[8px] w-[106px] text-bodyBB"}
                          >
                            Edit Stock
                          </Button>
                        </div>
                      )}
                      <div className="flex gap-1 items-center ">
                        {index ===
                          formik.values.treatment_done_info.length - 1 && (
                          <div onClick={addRow}>
                            <IoIosAddCircle className="w-8 h-8 text-success cursor-pointer" />
                          </div>
                        )}
                      </div>

                      <div onClick={() => removeRow(index)} className=" pt-1">
                        <RedClose />
                      </div>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceMedicineInfo;
